<template>
  <div class="calendar-container">
    <div class="calendar-city-select">
      <CityOfficeSelect v-model="selectedBranchId" :disabled="isSingleLocation" />
    </div>
    <!-- ВЕРХ: DateColumn + Header -->
    <div class="calendar-header">
      <div class="date-header">
        <DateColumn v-model="selectedDate" />
      </div>
      <div class="header-scroll-x" ref="headerScroll">
        <HeaderCabinetsAndEmployees
          :rooms-with-employees="roomsWithEmployees"
          :emp-col-width="EMP_COL_WIDTH"
          :scrollbar-width="scrollbarWidth"
        />
      </div>
    </div>
    <!-- НИЗ: TimeScale + грид (единый скроллируемый блок) -->
    <div class="calendar-main-scroll" ref="mainScroll" @scroll="onGridScroll">
      <div class="time-scale-column" ref="timeScaleColumn">
        <TimeScale :marks="timeMarks" />
      </div>
      <div class="grid-column">
        <ScheduleGrid
          :rooms-with-employees="roomsWithEmployees"
          :time-marks="timeMarks"
          :appointments="appointmentsForCurrentBranch"
          :current-date="selectedDate"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DateColumn from '@/components/util/calendar/DateColumn.vue'
import TimeScale from '@/components/util/calendar/TimeScale.vue'
import ScheduleGrid from '@/components/util/calendar/ScheduleGrid.vue'
import HeaderCabinetsAndEmployees from '@/components/util/calendar/HeaderCabinetsAndEmployees.vue'
import CityOfficeSelect from '@/components/util/calendar/CityOfficeSelect.vue'
import { mapActions, mapState } from 'vuex'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Calendar',
  components: {
    CityOfficeSelect,
    DateColumn,
    TimeScale,
    ScheduleGrid,
    HeaderCabinetsAndEmployees
  },
  data() {
    return {
      scrollbarWidth: 0,
      gridScrollLeft: 0,
      selectedBranchId: null,
      selectedDate: new Date().toISOString().slice(0, 10),
      EMP_COL_WIDTH: 240
    }
  },
  computed: {
    ...mapState('cities', ['cities']),
    ...mapState('auth', ['user']),
    ...mapState('schedules', ['schedules']),
    ...mapState('appointments', ['appointments']),
    isSingleLocation() {
      if (!this.cities || !this.cities.length) return false
      const branches = this.cities.flatMap(c => c.children || [])
      if (branches.length !== 1) return false
      const rooms = branches[0].children || []
      return rooms.length === 1
    },
    appointmentsForCurrentBranch() {
      // Тут можно фильтровать по городу, офису (филиалу), дате, если вдруг appointments не фильтруется на сервере
      if (!this.selectedBranchId || !this.selectedDate) return [];
      return (this.appointments || []).filter(a =>
        a.branch_id === this.selectedBranchId &&
        a.date === this.selectedDate
      );
    },
    currentBranch() {
      let found = null
      for (const city of this.cities) {
        if (!city.children) continue
        const branch = city.children.find(b => b.id === this.selectedBranchId)
        if (branch) {
          found = branch
          break
        }
      }
      return found
    },
    currentCityId() {
      for (const city of this.cities) {
        if (!city.children) continue
        const branch = city.children.find(b => b.id === this.selectedBranchId)
        if (branch) return city.id
      }
      return null
    },
    roomsWithEmployees() {
      if (!this.currentBranch) return []
      const rooms = this.currentBranch.children || []
      const daySchedules = this.schedules.flatMap(emp => {
        return (emp.shifts || [])
          .filter(shift =>
            shift.branch_id === this.currentBranch.id &&
            shift.date === this.selectedDate
          )
          .map(shift => ({
            ...shift,
            user_id: emp.id,
            user_name: emp.name,
            room_id: emp.room_id,
            room_name: emp.room_name
          }))
      })
      return rooms
        .map(room => ({
          room,
          employees: daySchedules.filter(sch => sch.room_id === room.id)
        }))
        .filter(entry => entry.employees.length)
    },
    timeMarks() {
      if (!this.currentBranch) return []
      const { start = '08:00', end = '21:00' } = this.currentBranch.working || {}
      const [h1, m1] = start.split(':').map(Number)
      const [h2, m2] = end.split(':').map(Number)
      const startMin = h1 * 60 + m1
      const endMin = h2 * 60 + m2
      const arr = []
      for (let t = startMin; t <= endMin; t += this.user.cell_interval) arr.push(t)
      return arr
    }
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (!newUser) return
        const payload = newUser.is_admin ? {} : { user_id: newUser.id }
        this.getCities(payload).then(() => {
          if (!this.selectedBranchId) {
            for (const city of this.cities) {
              if (city.children && city.children.length) {
                this.selectedBranchId = city.children[0].id
                break
              }
            }
          }
        })
      }
    },
    selectedBranchId(newBranchId) {
      if (!newBranchId) return
      this.fetchCalendarSchedules({
        date: this.selectedDate,
        branch_id: newBranchId,
        city_id: this.currentCityId
      })
      // Найди нужный город и подгрузи услуги
      const cityId = this.currentCityId
      if (cityId) {
        this.fetchServices({ city_id: cityId });
        // Загружаем записи
        this.fetchAppointments({
          date: this.selectedDate,
          city_id: cityId,
          branch_id: newBranchId
        })
      }
    },
    selectedDate(newDate) {
      if (!this.selectedBranchId) return
      this.fetchCalendarSchedules({
        date: newDate,
        branch_id: this.selectedBranchId,
        city_id: this.currentCityId
      })
      const cityId = this.currentCityId
      if (cityId) {
        // Загружаем записи
        this.fetchAppointments({
          date: newDate,
          city_id: cityId,
          branch_id: this.selectedBranchId
        })
      }
    }
  },
  mounted() {
    // Определяем ширину скроллбара
    this.scrollbarWidth = this.getScrollbarWidth()
    this.fetchCustomFields({ entity: 'appointment' })
  },
  methods: {
    ...mapActions('cities', ['getCities']),
    ...mapActions('schedules', ['fetchCalendarSchedules']),
    ...mapActions('services', ['fetchServices']),
    ...mapActions('appointments', ['fetchAppointments']),
    ...mapActions('custom_fields', ['fetchCustomFields']),
    getMaxScrollTopByTimeScale(containerEl) {
      const timeScaleEl = this.$refs.timeScaleColumn
      if (!containerEl || !timeScaleEl) return 0
      return Math.max(0, timeScaleEl.scrollHeight - containerEl.clientHeight)
    },
    clampVerticalScrollToTimeScale() {
      const containerEl = this.$refs.mainScroll
      if (!containerEl) return
      const maxScrollTop = this.getMaxScrollTopByTimeScale(containerEl)
      if (containerEl.scrollTop > maxScrollTop) {
        containerEl.scrollTop = maxScrollTop
      }
    },
    onGridScroll(e) {
      const maxScrollTop = this.getMaxScrollTopByTimeScale(e.target)
      if (e.target.scrollTop > maxScrollTop) {
        e.target.scrollTop = maxScrollTop
      }
      this.gridScrollLeft = e.target.scrollLeft
      if (this.$refs.headerScroll) {
        this.$refs.headerScroll.scrollLeft = this.gridScrollLeft
      }
    },
    getScrollbarWidth() {
      // Самый универсальный способ
      const outer = document.createElement('div')
      outer.style.visibility = 'hidden'
      outer.style.width = '100px'
      outer.style.msOverflowStyle = 'scrollbar'
      document.body.appendChild(outer)

      const widthNoScroll = outer.offsetWidth
      outer.style.overflow = 'scroll'

      const inner = document.createElement('div')
      inner.style.width = '100%'
      outer.appendChild(inner)

      const widthWithScroll = inner.offsetWidth
      outer.parentNode.removeChild(outer)
      return widthNoScroll - widthWithScroll
    }
  },
  updated() {
    this.clampVerticalScrollToTimeScale()
  }
}
</script>

<style scoped>
.calendar-container {
  --ui-color-bg-base: var(--cal-bg-panel);
  --ui-color-bg-weak: var(--cal-bg-app);
  --ui-color-bg-card: var(--cal-bg-panel-alt);
  --ui-color-white: var(--cal-bg-panel-alt);
  --ui-color-white-100: var(--cal-bg-panel-alt);
  --ui-color-border-default: var(--cal-border);
  --ui-color-border-soft: var(--cal-border-soft);
  --ui-color-text-primary: var(--cal-text-primary);
  --ui-color-text-regular: var(--cal-text-regular);
  --ui-color-text-secondary: var(--cal-text-muted);
  --ui-color-text-muted: var(--cal-text-muted);
  --ui-color-text-dim: var(--cal-text-dim);
  --ui-color-black: var(--cal-text-primary);

  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
  min-height: 0;
  border-bottom: 1px solid var(--ui-color-border-default);
  background: radial-gradient(120% 100% at 10% 0%, #0d2a66 0%, #061432 34%, #040d24 100%);
}

.calendar-city-select {
  position: absolute;
  top: 12px;
  right: 18px;
  z-index: 30;
  padding: 0;
  background: transparent;
  display: flex;
  justify-content: flex-end;
}

.calendar-header {
  display: flex;
  width: 100%;
  background: transparent;
  border-bottom: 1px solid var(--ui-color-border-default);
  border-top: 1px solid var(--ui-color-border-default);
  padding-top: 8px;
}

.date-header {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
  background: transparent;
  border-right: 1px solid var(--ui-color-border-default);
}

.header-scroll-x {
  flex: 1 1 0;
  overflow-x: hidden;
  min-height: 106px;
}

.calendar-main-scroll {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  padding-top: 0;
  background: transparent;
}

.time-scale-column {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
  background: transparent;
  border-right: 1px solid var(--ui-color-border-default);
  flex-shrink: 0;
}

.grid-column {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  background: transparent;
}

.calendar-container :deep(.el-select__wrapper) {
  background: linear-gradient(180deg, #102f6f 0%, #0a2457 100%);
  box-shadow: 0 0 0 1px #1a4b9e inset;
  border-radius: 8px;
  min-height: 38px;
  width: min(420px, 44vw);
}

.calendar-container :deep(.el-select__placeholder),
.calendar-container :deep(.el-select__selected-item),
.calendar-container :deep(.el-select__caret) {
  color: var(--cal-text-primary) !important;
}

.calendar-container :deep(.el-input__wrapper) {
  background: var(--cal-bg-panel-alt);
  box-shadow: 0 0 0 1px var(--cal-border-soft) inset;
}
</style>
